<script setup lang="ts">
/**
 * Pill text input with the template's dark hairline and a bold label above.
 * Drop-in for CommonInput on marketing, auth and account pages; the CV editor
 * keeps its own fields.
 */
const props = withDefaults(defineProps<{
  label?: string
  id?: string
  type?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  autocomplete?: string
  error?: string
  hint?: string
  /** Larger input for hero-style forms. */
  size?: 'md' | 'lg'
  min?: string | number
  max?: string | number
  step?: string | number
  maxlength?: number
  name?: string
  inputmode?: 'text' | 'email' | 'tel' | 'url' | 'numeric' | 'decimal' | 'search'
}>(), {
  type: 'text',
  size: 'md',
})

const model = defineModel<string | number | null>()

// useId() matches between the server render and the browser; a random id did not (hydration mismatch).
const fallbackId = `sofax-input-${useId()}`
const inputId = computed(() => props.id || fallbackId)
</script>

<template>
  <div>
    <label v-if="label" :for="inputId" class="field-label">
      {{ label }}<span v-if="required" class="text-accent"> *</span>
    </label>
    <input
      :id="inputId"
      v-model="model"
      :type="type"
      :name="name"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :min="min"
      :max="max"
      :step="step"
      :maxlength="maxlength"
      :inputmode="inputmode"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="error || hint ? `${inputId}-desc` : undefined"
      class="field-input"
      :class="[{ 'field-error': !!error }, size === 'lg' ? 'py-5 text-lg' : '']"
    >
    <span v-if="error" :id="`${inputId}-desc`" class="field-hint !text-red-600 dark:!text-red-400">{{ error }}</span>
    <span v-else-if="hint" :id="`${inputId}-desc`" class="field-hint">{{ hint }}</span>
  </div>
</template>
